import { useEffect, useRef } from 'react';
import { useAppDispatch, useAppSelector } from 'hooks/useAppStore';
import { setCredentials, logout, setLoading } from 'features/auth/authSlice';
import { getSupabase, isSupabaseConfigured } from 'shared/supabase/client';
import { getSessionAuth } from 'features/auth/api/authService';
import { logAuthSignIn } from 'shared/lib/learnerActivityLog';

async function hydrateSession(
  dispatch: ReturnType<typeof useAppDispatch>,
  { logoutOnFailure = true }: { logoutOnFailure?: boolean } = {},
) {
  try {
    const result = await getSessionAuth();
    if (result) {
      dispatch(setCredentials(result));
      return true;
    }
  } catch (err) {
    console.error('Failed to load platform profile after sign-in:', err);
    if (logoutOnFailure) {
      dispatch(logout());
    }
  }
  return false;
}

export function AuthInitializer({ children }: { children: React.ReactNode }) {
  const dispatch = useAppDispatch();
  const isAuthenticated = useAppSelector((s) => s.auth.isAuthenticated);
  const sessionEstablishedRef = useRef(isAuthenticated);

  useEffect(() => {
    if (isAuthenticated) {
      sessionEstablishedRef.current = true;
    }
  }, [isAuthenticated]);

  useEffect(() => {
    if (!isSupabaseConfigured) return;

    const supabase = getSupabase();
    const shouldBlockUi = !sessionEstablishedRef.current;
    if (shouldBlockUi) {
      dispatch(setLoading(true));
    }

    hydrateSession(dispatch)
      .then((ok) => {
        if (ok) sessionEstablishedRef.current = true;
      })
      .finally(() => {
        if (shouldBlockUi) {
          dispatch(setLoading(false));
        }
      });

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (event === 'SIGNED_OUT' || !session) {
        sessionEstablishedRef.current = false;
        dispatch(logout());
        return;
      }

      if (event === 'INITIAL_SESSION') {
        if (!sessionEstablishedRef.current) {
          // Initial hydrateSession (above) owns first-load UI.
          return;
        }
        await hydrateSession(dispatch, { logoutOnFailure: false });
        return;
      }

      if (event === 'TOKEN_REFRESHED' || event === 'SIGNED_IN') {
        // Supabase emits SIGNED_IN on tab visibility recovery (_recoverAndRefresh).
        if (sessionEstablishedRef.current) {
          await hydrateSession(dispatch, { logoutOnFailure: false });
          return;
        }

        dispatch(setLoading(true));
        const ok = await hydrateSession(dispatch);
        if (ok) {
          sessionEstablishedRef.current = true;
          logAuthSignIn(event);
        }
        dispatch(setLoading(false));
      }
    });

    return () => subscription.unsubscribe();
  }, [dispatch]);

  return <>{children}</>;
}
